import styled, { css } from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import type { PersonDetailTagTone } from "web-apps/company/components/timeReportsV2/components/views/person/models/personView.types";
import {
  DETAILED_ROW_COLUMN_GAP,
  DETAILED_ROW_HORIZONTAL_PADDING_PX,
  getDetailedGridColumns,
} from "./personRow.layout.logic";

export const RowCard = styled.div`
  border-radius: 20px;
  background: ${ColorV2.White};
  padding: 12px 16px;
  margin-top: 16px;
`;

export const SummaryGrid = styled.div<{
  readonly $hasDeviation: boolean;
  readonly $isDetailed: boolean;
  readonly $metricColumns: number;
}>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.$isDetailed
      ? getDetailedGridColumns(props.$hasDeviation)
      : `minmax(260px, 2.3fr) repeat(${props.$metricColumns}, minmax(84px, 1fr)) 72px`};
  column-gap: ${(props) =>
    props.$isDetailed ? DETAILED_ROW_COLUMN_GAP : "10px"};
  padding: 0
    ${(props) => (props.$isDetailed ? DETAILED_ROW_HORIZONTAL_PADDING_PX : 0)}px;
  align-items: center;
`;

export const ActionButtons = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  width: 30px;
  height: 30px;
  padding: 0;
  cursor: pointer;
`;

export const DetailsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 14px;
`;

export const SelectAllRow = styled.div<{ readonly $isDetailed: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 0
    ${(props) => (props.$isDetailed ? DETAILED_ROW_HORIZONTAL_PADDING_PX : 4)}px;
`;

export const DetailRow = styled.div`
  border-radius: 16px;
  background: ${ColorV2.Blue7};
  padding: 14px 18px;
  display: grid;
  grid-template-columns:
    28px
    minmax(260px, 2.3fr)
    repeat(6, minmax(84px, 1fr))
    minmax(220px, 1.5fr)
    24px;
  column-gap: 12px;
  align-items: center;
  cursor: pointer;

  &:hover {
    background: #eaf1f7;
  }

  &:focus-visible {
    outline: 2px solid ${ColorV2.Blue1};
    outline-offset: 2px;
  }
`;

export const DetailCheckboxCell = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  grid-column: 1;
`;

export const DetailDateColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  grid-column: 2;
`;

export const DetailDate = styled.span`
  ${TypographyV2.b1};
  color: ${ColorV2.Blue1};
`;

export const DetailWeekday = styled.span`
  ${TypographyV2.s1};
  color: ${ColorV2.Blue4};
`;

export const DetailTimeColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  grid-column: 7;
`;

export const DetailTotal = styled.span`
  ${TypographyV2.b1};
  color: ${ColorV2.Blue1};
`;

export const DetailRange = styled.span`
  ${TypographyV2.s1};
  color: ${ColorV2.Blue4};
  white-space: nowrap;
`;

export const StatusChip = styled.span<{ $tone: PersonDetailTagTone }>`
  ${TypographyV2.s1};
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  padding: 8px 14px;
  white-space: nowrap;
  width: fit-content;
  justify-self: end;

  ${(props) =>
    props.$tone === "danger" &&
    css`
      background: #fce9e9;
      color: ${ColorV2.NegativeRed};
    `}

  ${(props) =>
    props.$tone === "warning" &&
    css`
      background: #f9f0db;
      color: ${ColorV2.DarkAmbigiousYellow};
    `}

  ${(props) =>
    props.$tone === "info" &&
    css`
      background: rgba(45, 141, 219, 0.14);
      color: ${ColorV2.ElectricBlue};
    `}

  ${(props) =>
    props.$tone === "purple" &&
    css`
      background: #ebe7fb;
      color: ${ColorV2.AbsentPurple};
    `}

  ${(props) =>
    props.$tone === "success" &&
    css`
      background: #e1f2e9;
      color: ${ColorV2.PositiveGreen};
    `}
`;

export const StatusColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  grid-column: 9;
  justify-self: end;
`;

export const StatusChipRow = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: nowrap;
`;

export const StatusMeta = styled.span`
  ${TypographyV2.s1};
  color: ${ColorV2.Blue4};
`;
